import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useBoolean, useSetState } from 'react-use'

import { ColumnTypes } from '@makyoapp/makyo-sdk/models/Table'
import {
  ALL_FUNCTIONS_IDS,
  ColumnAdapter,
  DEFAULT_ADR_COL_OPTIONS,
  DEFAULT_BARCODE_COL_OPTIONS,
  DEFAULT_COLOR_COL_OPTIONS,
  DEFAULT_CONN_COL_OPTIONS,
  DEFAULT_DATETIME_COL_OPTIONS,
  DEFAULT_FORMULA_COL_OPTIONS,
  DEFAULT_FUNCTION_COL_OPTIONS,
  DEFAULT_ICON_COL_OPTIONS,
  DEFAULT_IMG_COL_OPTIONS,
  DEFAULT_JOIN_COL_OPTIONS,
  DEFAULT_LOOKUP_COL_OPTIONS,
  DEFAULT_MATH_COL_OPTIONS,
  DEFAULT_NUMBER_COL_OPTIONS,
  DEFAULT_PHONE_COL_OPTIONS,
  DEFAULT_QRCODE_COL_OPTIONS,
  DEFAULT_RATING_COL_OPTIONS,
  DEFAULT_SEO_AI_COL_OPTIONS,
  DEFAULT_SLUG_COL_OPTIONS,
  DEFAULT_TEXT_PROC_AI_COL_OPTIONS,
  QRCodeType,
} from '@makyoapp/makyo-common'

import { CreateColumnValue } from '@/shared/fx-parser'
import { useGridV2Context } from '@/components/GridV2/contexts/GridV2Context'

export interface BasicColumnTypeInfo {
  type: ColumnTypes
  name: string
  description: string
  keywords: string[]
  isPlaceholder?: boolean
}

export const BASIC_COLUMN_TYPES: BasicColumnTypeInfo[] = [
  {
    type: ColumnTypes.Text,
    name: 'colType.text',
    description: 'colType.text.description',
    keywords: ['text', 'title', 'name'],
  },
  {
    type: ColumnTypes.Number,
    name: 'colType.number',
    description: 'colType.number.description',
    keywords: ['number', 'decimal', 'integer', 'int', 'double', 'float', 'num'],
  },
  {
    type: ColumnTypes.DateTime,
    name: 'colType.datetime',
    description: 'colType.datetime.description',
    keywords: ['date', 'time', 'calendar', 'year', 'month', 'day', 'week'],
  },
  {
    type: ColumnTypes.Boolean,
    name: 'colType.boolean',
    description: 'colType.boolean.description',
    keywords: ['true', 'false', 'bool', 'boolean'],
  },
  {
    type: ColumnTypes.URL,
    name: 'colType.url',
    description: 'colType.url.description',
    keywords: ['url', 'link', 'www', 'http', 'site'],
  },
  {
    type: ColumnTypes.Image,
    name: 'colType.image',
    description: 'colType.image.description',
    keywords: ['image', 'picture', 'photo', 'gallery'],
  },
  {
    type: ColumnTypes.ImageArray,
    name: 'colType.imagearray',
    description: 'colType.imagearray.description',
    keywords: ['image', 'picture', 'photo', 'gallery'],
  },
  {
    type: ColumnTypes.Email,
    name: 'colType.email',
    description: 'colType.email.description',
    keywords: ['email', 'mail', 'address'],
  },
  {
    type: ColumnTypes.Phone,
    name: 'colType.phone',
    description: 'colType.phone.description',
    keywords: ['phone', 'telephone', 'number', 'mobile', 'fax'],
  },
  {
    type: ColumnTypes.RichText,
    name: 'colType.richtext',
    description: 'colType.richtext.description',
    keywords: ['rich', 'text', 'markdown', 'long'],
  },
  {
    type: ColumnTypes.RowID,
    name: 'colType.rowid',
    description: 'colType.rowid.description',
    keywords: ['unique', 'rowid', 'guid', 'uuid', 'primary', 'key', 'random'],
  },
  {
    type: ColumnTypes.MultiSelect,
    name: 'colType.multiselect',
    description: 'colType.multiselect.description',
    keywords: ['select', 'options', 'multiple', 'multi'],
  },
  {
    type: ColumnTypes.Array,
    name: 'colType.array',
    description: 'colType.array.description',
    keywords: ['array', 'options', 'string'],
  },
  {
    type: ColumnTypes.Attachment,
    name: 'colType.attachment',
    description: 'colType.attachment.description',
    keywords: ['attachment', 'file', 'upload'],
  },
  {
    type: ColumnTypes.Rating,
    name: 'colType.rating',
    description: 'colType.rating.description',
    keywords: ['star', 'fav', 'rate', 'rating', 'review'],
  },
  {
    type: ColumnTypes.Icon,
    name: 'colType.icon',
    description: 'colType.icon.description',
    keywords: ['icon', 'design', 'material', 'variant', 'image'],
  },
  {
    type: ColumnTypes.Address,
    name: 'colType.address',
    description: 'colType.address.description',
    keywords: ['address', 'location', 'street', 'map', 'plus'],
  },
  {
    type: ColumnTypes.Coordinate,
    name: 'colType.geo-coordinate',
    description: 'colType.geo-coordinate.description',
    keywords: ['location', 'gps', 'point', 'coord', 'map', 'geo'],
  },
  {
    type: ColumnTypes.Slug,
    //icon: 'LinkAlt16',
    name: 'colType.slug',
    description: 'colType.slug.description',
    keywords: ['slug', 'url', 'link', 'id', 'ident'],
  },
  /*{
   type: ExtendedColumnTypes.PageURL,
   color: 'bg-fuchsia-200',
   icon: 'GoToScreen16',
   name: 'colType.page-url',
   description: 'colType.page-url.description',
   keywords: ['page', 'screen', 'url', 'link']
   },*/
  {
    type: ColumnTypes.AISEO,
    name: 'colType.seo-ai',
    description: 'colType.seo-ai.description',
    keywords: ['seo', 'search', 'engine', 'optimize', 'ai', 'artificial', 'intelligence', 'key'],
  },
  {
    type: ColumnTypes.AITextProcessing,
    name: 'colType.text-processing-ai',
    description: 'colType.text-processing-ai.description',
    keywords: ['text', 'ai', 'artificial', 'intelligence', 'process'],
  },
  {
    type: ColumnTypes.QRCode,
    name: 'colType.qrcode',
    description: 'colType.qrcode.description',
    keywords: ['code', 'qr', 'qrcode', 'barcode'],
  },
  {
    type: ColumnTypes.Barcode,
    name: 'colType.barcode',
    description: 'colType.barcode.description',
    keywords: ['code', 'barcode'],
  },
  {
    type: ColumnTypes.Color,
    name: 'colType.color',
    description: 'colType.color.description',
    keywords: ['color', 'picker'],
  },
  {
    type: ColumnTypes.Connection,
    name: 'colType.connection',
    description: 'colType.connection.description',
    keywords: ['relation', 'connection'],
  },
  {
    type: ColumnTypes.Lookup,
    name: 'colType.lookup',
    description: 'colType.lookup.description',
    keywords: ['relation', 'lookup'],
  },
  {
    type: ColumnTypes.Join,
    name: 'colType.join',
    description: 'colType.join.description',
    keywords: ['join', 'string', 'concat', 'combine'],
  },
  {
    type: ColumnTypes.Function,
    name: 'colType.function',
    description: 'colType.function.description',
    keywords: ['formula', 'function', ...ALL_FUNCTIONS_IDS],
  },
  {
    type: ColumnTypes.Formula,
    name: 'colType.formula',
    description: 'colType.formula.description',
    keywords: ['formula', 'function'],
  },
  {
    type: ColumnTypes.Math,
    name: 'colType.math',
    description: 'colType.math.description',
    keywords: ['math', 'calculation'],
  },
  {
    type: ColumnTypes.Template,
    name: 'colType.template',
    description: 'colType.template.description',
    keywords: ['template', 'text', 'string'],
  },
  // todo -> add back in after being implemented
  {
    type: ColumnTypes.SingleCell,
    name: 'colType.single-cell',
    description: 'colType.single-cell.description',
    keywords: ['single', 'cell', 'single-cell'],
  },
]

export interface CreateColumnContextState {
  /**
   * this context id
   */
  id: string
  /**
   * the value
   */
  value: CreateColumnValue
  /**
   * set the value
   */
  setValue: (patch: Partial<CreateColumnValue> | ((prevState: CreateColumnValue) => Partial<CreateColumnValue>)) => void
  /**
   * error message
   */
  error: { [k: string]: string }
  /**
   * set error message
   */
  setError: (patch: Partial<Record<string, string>>) => void
  /**
   * reset the value
   */
  reset: () => void
  /**
   * is loading state
   */
  isLoading: boolean
  /**
   * set is loading state
   */
  setIsLoading: (nextLoading: boolean) => void
  /**
   * is button disabled state
   */
  buttonDisabled: boolean
  /**
   * for column type that require multistep
   */
  step: number
  /**
   * for column type that require multistep
   */
  setStep: (next: number) => void

  lookupCols: ColumnAdapter[]
  setLookupCols: (lookupCols: ColumnAdapter[]) => void
  /**
   * initial column type in edit mode
   */
  initialType: ColumnTypes
}

const defaultValue: CreateColumnValue = {
  type: BASIC_COLUMN_TYPES[0].type,
  //isUserSpecific: false,
  name: '',
  numberOptions: {
    ...DEFAULT_NUMBER_COL_OPTIONS,
  },
  dtOptions: {
    ...DEFAULT_DATETIME_COL_OPTIONS,
  },
  phoneOptions: {
    ...DEFAULT_PHONE_COL_OPTIONS,
  },
  addressOptions: {
    ...DEFAULT_ADR_COL_OPTIONS,
  },
  imageOptions: {
    ...DEFAULT_IMG_COL_OPTIONS,
  },
  icOptions: {
    ...DEFAULT_ICON_COL_OPTIONS,
  },
  ratingOptions: {
    ...DEFAULT_RATING_COL_OPTIONS,
  },
  slugOptions: {
    ...DEFAULT_SLUG_COL_OPTIONS,
  },
  seoAiOptions: {
    ...DEFAULT_SEO_AI_COL_OPTIONS,
  },
  textProcOptions: {
    ...DEFAULT_TEXT_PROC_AI_COL_OPTIONS,
  },
  qrOptions: {
    ...DEFAULT_QRCODE_COL_OPTIONS,
  },
  barcodeOptions: {
    ...DEFAULT_BARCODE_COL_OPTIONS,
  },
  colorOptions: {
    ...DEFAULT_COLOR_COL_OPTIONS,
  },
  connectionOptions: {
    ...DEFAULT_CONN_COL_OPTIONS,
  },
  lookupOptions: {
    ...DEFAULT_LOOKUP_COL_OPTIONS,
    standard: false,
  },
  joinOptions: {
    ...DEFAULT_JOIN_COL_OPTIONS,
  },
  formulaOptions: {
    ...DEFAULT_FORMULA_COL_OPTIONS,
  },
  functionOptions: {
    ...DEFAULT_FUNCTION_COL_OPTIONS,
  },
  mathOptions: {
    ...DEFAULT_MATH_COL_OPTIONS,
  },
  templateOptions: {
    tmpl: '{{x}} and {{y}}',
    vars: ['x', 'y'],
    columns: ['', ''],
  },
  singleCellOptions: {
    tblId: '',
    colId: '',
    rowOptions: 'first',
    row: '',
  },
}

const CreateColumnContext = createContext<CreateColumnContextState>({
  id: '',
  value: defaultValue,
  error: {},
  setError: () => null,
  setValue: () => null,
  reset: () => null,
  isLoading: false,
  setIsLoading: () => null,
  buttonDisabled: false,
  step: 1,
  setStep: () => null,
  lookupCols: [],
  setLookupCols: () => null,
  initialType: defaultValue.type,
})

/**
 * use the context
 */
export function useCreateColumnContext() {
  return useContext(CreateColumnContext)
}

/**
 * Create Column provider
 */
export const CreateColumnProvider = ({
  children,
  id,
  initialValue,
}: {
  children?: React.ReactNode
  id: string
  initialValue?: CreateColumnValue
}) => {
  const { activeTable } = useGridV2Context()

  const getDefaultValue = useCallback(() => {
    let defaultVal = initialValue ? { ...initialValue } : defaultValue

    // only allow advanced columns in table query
    if (activeTable?.isQuery) {
      // @ts-ignore
      defaultVal.type = initialValue?.type || ''
    }

    return defaultVal
  }, [activeTable?.isQuery, initialValue])

  const [value, setValue] = useSetState(initialValue || defaultValue)
  const [initialType] = useState(initialValue?.type || defaultValue.type)

  const [error, setError] = useSetState({})
  const [isLoading, setIsLoading] = useBoolean(false)
  const [step, setStep] = useState(1)

  const isDisableButton = useCallback(() => {
    if (!value.type) return true

    switch (value.type) {
      case ColumnTypes.Image: {
        const srcType = value.imageOptions.imgSourceType
        if (srcType === 'gravatar') {
          return !Boolean(value.imageOptions.imgSourceGravatar)
        }
        if (srcType === 'youtube') {
          return !Boolean(value.imageOptions.imgSourceYoutube)
        }
        if (srcType === 'random') {
          return !Boolean(value.imageOptions.imgKeywords)
        }
        return false
      }
      case ColumnTypes.Slug:
        return !Boolean(value.slugOptions.slugColumn)
      case ColumnTypes.AISEO:
        return !Boolean(value.seoAiOptions.seoAiColumn)
      case ColumnTypes.AITextProcessing:
        return !Boolean(value.textProcOptions.textProcColumn)
      case ColumnTypes.QRCode:
        switch (value.qrOptions.qrType) {
          case QRCodeType.VCard:
            return (
              [value.qrOptions.qrVCard.firstName, value.qrOptions.qrVCard.lastName]
                //.map((v) => Boolean(v))
                .some((v) => !Boolean(v))
            )
          case QRCodeType.Email:
            return !Boolean(value.qrOptions.qrEmail.to)
          /*return Object.values(value.qrOptions.qrEmail)
              .map((v) => Boolean(v))
              .some((v) => !v)*/
          case QRCodeType.SMS:
            /*return Object.values(value.qrOptions.qrSMS)
              .map((v) => Boolean(v))
              .some((v) => !v)*/
            return !Boolean(value.qrOptions.qrSMS.to)
          case QRCodeType.Text:
            return !Boolean(value.qrOptions.qrText)
          case QRCodeType.URL:
            return !Boolean(value.qrOptions.qrURL)
          case QRCodeType.File:
            return !Boolean(value.qrOptions.qrFile)
          case QRCodeType.Crypto:
            return (
              [value.qrOptions.qrCrypto.amount, value.qrOptions.qrCrypto.receiver, value.qrOptions.qrCrypto.crypto]
                //.map((v) => Boolean(v))
                .some((v) => !Boolean(v))
            )
        }
        break
      case ColumnTypes.Lookup:
        return [value.lookupOptions.lookupConnColumnId, value.lookupOptions.lookupColumnId].some((v) => !Boolean(v))
      case ColumnTypes.Connection:
        if (step < 2) {
          return [
            value.connectionOptions.connFTableId,
            value.connectionOptions.connFColId,
            value.connectionOptions.connColId,
          ].some((v) => !Boolean(v))
        } else {
          return false
        }
      case ColumnTypes.Formula:
        return !Boolean(value.formulaOptions.formulaValue)
      case ColumnTypes.Math: {
        if (value.mathOptions) {
          const { vars, columns } = value.mathOptions

          if (!value.mathOptions.expr) return true

          if (!!vars.length) {
            if (!columns?.every((col) => col?.trim().length > 0)) return true
            if (vars.length !== columns?.length) return true
          }
        }
        return false
      }
      case ColumnTypes.Template: {
        if (!value.templateOptions) return true
        const { tmpl, vars, columns } = value.templateOptions

        // check value is exist
        if (!tmpl) return true

        // check all columns must have value
        if (columns?.length && !columns?.every((col) => col?.trim().length > 0)) return true

        // check available variables must be equal to columns
        if (vars.length !== columns?.length) return true
        return false
      }
      case ColumnTypes.SingleCell: {
        if (!value.singleCellOptions) return true
        const { tblId, colId, rowOptions } = value.singleCellOptions
        if (!tblId) return true
        if (!colId) return true
        if (!rowOptions) return true
        return false
      }
      default:
        return false
    }
  }, [value, step])

  const [buttonDisabled, setButtonDisabled] = useState(isDisableButton())

  useEffect(() => {
    setButtonDisabled(isDisableButton())
  }, [isDisableButton])

  const [lookupCols, setLookupCols] = useState<ColumnAdapter[]>([])

  const reset = useCallback(() => {
    setStep(1)
    setError({})
    setValue(defaultValue)
    setLookupCols([])
  }, [setValue, setStep, setError, setLookupCols])

  return (
    <>
      <CreateColumnContext.Provider
        value={{
          value,
          setValue,
          reset,
          id,
          error,
          setError,
          isLoading,
          setIsLoading,
          buttonDisabled,
          step,
          setStep,
          lookupCols,
          setLookupCols,
          initialType,
        }}
      >
        {children}
      </CreateColumnContext.Provider>
    </>
  )
}
